const ATLASSIAN_CANONICAL_BASE = "https://www.atlassian.com";
export const SOURCETREEAPP_CANONICAL_BASE = "https://www.sourcetreeapp.com";
export const BITBUCKET_CANONICAL_BASE = "https://bitbucket.org";

const SOURCETREEAPP_MARKER = "sourcetreeapp";
const BITBUCKET_PATH_MARKER = "bitbucketapp";

const SUPPORTED_LOCALES = new Set([
    "br",
    "de",
    "en",
    "es",
    "fr",
    "it",
    "ja",
    "ko",
    "nl",
    "pl",
    "ru",
    "xx",
    "zh",
    "zz"
]);

function hostnameContainsSourcetreeApp(hostname: string): boolean {
    return hostname.toLowerCase().includes(SOURCETREEAPP_MARKER);
}

function hostnameContainsBitbucket(hostname: string): boolean {
    return hostname.toLowerCase().includes("bitbucket");
}

/**
 * Parsed route path (no leading slash), e.g. `sourcetreeapp` for `/sourcetreeapp`
 * or `ja/sourcetreeapp` for localized URLs.
 */
function pathContainsSourcetreeApp(pathWithoutLeadingSlash: string): boolean {
    return pathWithoutLeadingSlash.toLowerCase().includes(SOURCETREEAPP_MARKER);
}

/**
 * Returns true when `bitbucketapp` is the first path segment, or the second
 * segment after a supported locale prefix (e.g. `ja/bitbucketapp/product`).
 *
 * Paths like `software/bitbucketapp/pricing` return false because
 * `bitbucketapp` is not in the leading position.
 */
function isBitbucketAppPath(pathWithoutLeadingSlash: string): boolean {
    const segments = pathWithoutLeadingSlash
        .toLowerCase()
        .split("/")
        .filter((s) => s.length > 0);

    const first = segments[0];
    const second = segments[1];

    if (first === undefined) {
        return false;
    }

    if (first === BITBUCKET_PATH_MARKER) {
        return true;
    }

    if (
        second !== undefined &&
        SUPPORTED_LOCALES.has(first) &&
        second === BITBUCKET_PATH_MARKER
    ) {
        return true;
    }

    return false;
}

function stripSourcetreeAppPathSegments(segmentPath: string): string {
    return segmentPath
        .split("/")
        .filter(
            (segment) =>
                segment.length > 0 &&
                segment.toLowerCase() !== SOURCETREEAPP_MARKER
        )
        .join("/");
}

function stripBitbucketPathSegments(segmentPath: string): string {
    return segmentPath
        .split("/")
        .filter(
            (segment) =>
                segment.length > 0 &&
                segment.toLowerCase() !== BITBUCKET_PATH_MARKER
        )
        .join("/");
}

/**
 * Path segment for `rel="canonical"` / alternates when using the SourceTree origin.
 * Removes each `sourcetreeapp` path segment so marketing URLs like
 * `/ja/sourcetreeapp/download` do not become `.../ja/sourcetreeapp/download` on
 * www.sourcetreeapp.com (target: `.../ja/download`).
 *
 * Similarly removes `bitbucketapp` path segments for the Bitbucket origin.
 *
 * Returns `""` when the path was only marker segment(s) (use origin-only href).
 */
export function canonicalPathForHref(
    canonicalBaseUrl: string,
    segmentPath: string | undefined
): string | undefined {
    if (segmentPath === undefined || segmentPath === "") {
        return undefined;
    }
    if (canonicalBaseUrl === SOURCETREEAPP_CANONICAL_BASE) {
        return stripSourcetreeAppPathSegments(segmentPath);
    }
    if (canonicalBaseUrl === BITBUCKET_CANONICAL_BASE) {
        return stripBitbucketPathSegments(segmentPath);
    }

    return segmentPath;
}

/**
 * Full canonical or alternate URL for link tags.
 */
export function buildCanonicalLinkHref(
    canonicalBaseUrl: string,
    segmentPath: string | undefined
): string | undefined {
    if (segmentPath === undefined) {
        return undefined;
    }
    const rest = canonicalPathForHref(canonicalBaseUrl, segmentPath);
    if (rest === undefined) {
        return undefined;
    }
    return rest === "" ? canonicalBaseUrl : `${canonicalBaseUrl}/${rest}`;
}

/**
 * Origin for `<link rel="canonical">` and hreflang alternates.
 *
 * Uses `www.sourcetreeapp.com` when:
 * - The host name includes `sourcetreeapp` (e.g. `www.sourcetreeapp.com`), or
 * - The route path includes `sourcetreeapp` (e.g. `/sourcetreeapp` on www.atlassian.com).
 *
 * Uses `bitbucket.org` when:
 * - The host name includes `bitbucket` (e.g. `bitbucket.org`, `www.bitbucket.org`), or
 * - The route path starts with `bitbucketapp` (or `{locale}/bitbucketapp`) on atlassian.com.
 *   Plain `/bitbucket` paths on atlassian.com are NOT redirected.
 */
export function getCanonicalBaseUrl(
    hostname?: string,
    pathWithoutLeadingSlash?: string
): string {
    if (hostname && hostnameContainsSourcetreeApp(hostname)) {
        return SOURCETREEAPP_CANONICAL_BASE;
    }
    if (
        pathWithoutLeadingSlash &&
        pathContainsSourcetreeApp(pathWithoutLeadingSlash)
    ) {
        return SOURCETREEAPP_CANONICAL_BASE;
    }
    if (hostname && hostnameContainsBitbucket(hostname)) {
        return BITBUCKET_CANONICAL_BASE;
    }
    if (
        pathWithoutLeadingSlash &&
        isBitbucketAppPath(pathWithoutLeadingSlash)
    ) {
        return BITBUCKET_CANONICAL_BASE;
    }

    return ATLASSIAN_CANONICAL_BASE;
}
